import { CSSObject, CSSProperties } from 'styled-components';
import { BREAKPOINTS } from '@/styles/responsive';
import { HTMLAttributes } from 'react';

export const COMMON_STYLES = [
  'rowGap',
  'columnGap',
  'gap',
  'width',
  'position',
  'height',
  'cursor',
  'textAlign',
  'zIndex',
  'wordBreak',
  'right',
  'left',
  'top',
  'bottom',
  'background',
  'overflow',
  'minHeight',
  'minWidth',
  'color',
  'borderRadius',
  'border',
  'borderTop',
  'borderBottom',
  'borderLeft',
  'borderRight',
  'maxWidth',
  'gridTemplateColumns',
  'textTransform',
  'flex',
] as const;

type CommonStyleKey = (typeof COMMON_STYLES)[number];

export type CommonStylesType = {
  [K in CommonStyleKey]?: CSSProperties[K];
};

// Get all CSS properties that start with 'margin' or 'padding'
export type SpacingPropsType = Partial<{
  [K in keyof CSSProperties as K extends `margin${infer _}` | `padding${infer _}` ? K : never]: CSSProperties[K];
}>;

export type BoxResponsivePropsType = {
  mobile?: CSSProperties;
  tablet?: CSSProperties;
  tabletOnly?: CSSProperties;
};

export type BoxCustomPropsType = BoxResponsivePropsType & {
  focusable?: boolean;
  ellipsis?: boolean;
};

export type BoxWrapperPropsType = HTMLAttributes<HTMLDivElement> &
  SpacingPropsType &
  BoxCustomPropsType &
  CommonStylesType & {
    display?: CSSProperties['display'];
    justifyContent?: CSSProperties['justifyContent'];
    alignItems?: CSSProperties['alignItems'];
    flexDirection?: CSSProperties['flexDirection'];
    flexWrap?: CSSProperties['flexWrap'];
    flexGrow?: CSSProperties['flexGrow'];
    flexShrink?: CSSProperties['flexShrink'];
  };

export const getCommonStyles = (props: BoxWrapperPropsType) => {
  return COMMON_STYLES.reduce((result, currentValue) => {
    if (props[currentValue] !== undefined) {
      result[currentValue] = props[currentValue];
    }

    return result;
  }, {});
};

export const getSpacingStyles = (props: BoxWrapperPropsType) => {
  const spacingStyles: Record<string, unknown> = {};

  Object.keys(props).forEach((key) => {
    if (key.startsWith('padding') || key.startsWith('margin')) {
      spacingStyles[key] = props[key as keyof BoxWrapperPropsType];
    }
  });

  return spacingStyles;
};

const getDisplayStyles = ({ display, justifyContent, alignItems, flexDirection }: BoxWrapperPropsType) => {
  if (display) return display;

  return justifyContent || alignItems || flexDirection ? 'flex' : display;
};

const getEllipsisStyles = ({ ellipsis }: BoxWrapperPropsType) => {
  if (!ellipsis) return null;

  return {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
};

const getResponsiveStyles = (props: BoxWrapperPropsType) => {
  const { mobile, tablet, tabletOnly } = props;
  const styles = {};

  if (mobile) {
    styles[`@media screen and (max-width: ${BREAKPOINTS.md}px)`] = mobile || {};
  }

  if (tablet) {
    styles[`@media screen and (max-width: ${BREAKPOINTS.xl}px)`] = tablet || {};
  }

  if (tabletOnly) {
    styles[`@media screen and (min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.xl}px)`] =
      tabletOnly || {};
  }

  return styles;
};

export const boxStyles = (props: BoxWrapperPropsType): CSSObject => {
  const { justifyContent, alignItems, flexDirection, flexWrap, flexGrow, flexShrink, ...restProps } = props;
  const displayStyle = getDisplayStyles(props);
  const ellipsisStyles = getEllipsisStyles(props);
  const responsiveStyles = getResponsiveStyles(props);
  const spacingStyles = getSpacingStyles(props);
  const commonStyles = getCommonStyles(restProps);

  return {
    display: displayStyle,
    justifyContent,
    alignItems,
    flexDirection,
    flexWrap,
    flexGrow,
    flexShrink,
    ...commonStyles,
    ...spacingStyles,
    ...ellipsisStyles,
    ...responsiveStyles,
  };
};
